import { serverUrl } from './EsEnv';
import { ACCOUNT_DELETED_STORAGE, API_AUTH_USER, API_LOGOUT, CODEX_PICKS_STORAGE } from './EsConstants';
import { esRumError } from './EsRum';
import { esServerAnswered, esServerUnreachable } from './EsServerStatus';
import { esSessionAnswered, esSessionLeaving } from './EsSessionClock';

// The locale the reader picked, sent on every call while rolloutEsTranslations is on so esback answers
// a signed-out reader in it too (account, then this, then Accept-Language). EsLocaleProvider sets
// it; null sends no header, which is every call with the flag off.
export const LOCALE_HEADER = 'X-Es-Locale';
let gLocale = null;

export function esSetFetchLocale( pLocale ) {
    gLocale = pLocale || null;
}

// The demo table's Switch player: the seat's account id while a demo visitor sees the table as
// one of its seats, sent on every call so the console's reads and writes are all the seat's.
// esback's EsDemoSeatFilter decides which routes it reaches; null sends no header.
export const DEMO_AS_HEADER = 'X-Es-Demo-As';
let gDemoAs = null;

export function esSetFetchDemoAs( pAccountId ) {
    gDemoAs = pAccountId == null ? null : String( pAccountId );
}

// The table the console is open on, sent on every call it makes, the Codex screen's included: esback
// shows test data inside the live demo table's session alone (EsDemoSeatFilter.inDemoSession), and a
// codex read names no table of its own. esback reads the demo table's own packs through it, for a
// reader of its console, and ignores any other table; null sends no header.
export const TABLE_HEADER = 'X-Es-Table';
let gTable = null;

export function esSetFetchTable( pCampaignId ) {
    gTable = pCampaignId == null ? null : String( pCampaignId );
}

/** The seat being seen through, or null: for what cannot carry a header, as an <img> cannot. */
export function esFetchDemoAs() {
    return gDemoAs;
}

function sentHeaders( pHeaders ) {
    if ( !gLocale && !gDemoAs && !gTable ) return null;
    return {
        ...( gLocale ? { [ LOCALE_HEADER ]: gLocale } : {} ),
        ...( gDemoAs ? { [ DEMO_AS_HEADER ]: gDemoAs } : {} ),
        ...( gTable ? { [ TABLE_HEADER ]: gTable } : {} ),
        ...pHeaders,
    };
}

/**
 * fetch, aimed at esback.
 *
 * <p>Every fetch in this application talks to esback and none talks anywhere
 * else, so the two things each of them has to get right are settled here once
 * instead of thirty times.
 *
 * <p>**Where.** serverUrl is the page's own origin, everywhere -- a laptop
 * included since 2026-09-23, where the dev server proxies /api to esback the
 * way the ALB does. Before that a laptop was two origins and this was the one
 * place that knew it; half the app had once addressed the API and half the
 * proxy, indistinguishably until one of them broke, which is why every call
 * still comes through here.
 *
 * <p>**Credentials.** The session is a JSESSIONID cookie. Same-origin it is
 * sent whatever this says, so 'include' changes nothing today; it stays so a
 * call can never again silently lose its session if anything puts esback on
 * another origin -- the failure then was a 302 to the Cognito login that
 * looked nothing like a missing option.
 *
 * <p>A caller may still override either by passing its own value; the point is
 * that it has to say so.
 */
export function esFetch( pPath, pOptions = {} ) {
    const aHeaders = sentHeaders( pOptions.headers );
    const aOptions = aHeaders ? { ...pOptions, headers: aHeaders } : pOptions;
    return fetch( serverUrl + pPath, { credentials: 'include', ...aOptions } ).then(
        ( pResponse ) => {
            esServerAnswered( pResponse );
            esSessionAnswered( pResponse );
            return pResponse;
        },
        ( pError ) => {
            // An abort is the caller's choice, not esback going away.
            if ( pError?.name !== 'AbortError' ) esServerUnreachable();
            throw pError;
        } );
}

/**
 * A READ from esback, with the status checked before the body is touched.
 *
 * <p>The read half of {@link esWrite}, and it exists for the same reason that
 * one does. `esFetch( ... ).then( response => response.json() )` -- the pattern
 * this replaces, which was in nine admin screens -- has no status check at all,
 * so a refusal is parsed as though it were the thing that was asked for.
 *
 * <p>**What that looked like in production.** Spring Security answers a refusal
 * with an EMPTY BODY, so `response.json()` REJECTS on a 403. In a screen with a
 * `.catch` that set only loading and loaded, the empty table was drawn over the
 * refusal and the history page said "Nothing has happened yet" about being
 * forbidden. In a screen with NO catch at all -- three of the codex lists --
 * the rejection was unhandled and the spinner never stopped. And where the body
 * did parse, `{"msg": ...}` was set into state as if it were the row or the
 * array, which is what once turned a server-side sort error into
 * "value.map is not a function".
 *
 * <p>A refusal that reads as an absence is the worst of those, because nothing
 * about it looks wrong.
 *
 * <p>Answers the parsed body on 2xx and THROWS on anything else, carrying
 * esback's own {"msg": ...} when there is one and the status when there is not
 * -- the same contract as esWrite, so a caller can `.catch( e => e.message )`
 * whichever verb it used. An empty 2xx body answers null rather than failing to
 * parse.
 */
export async function esRead( pPath, pOptions = {} ) {
    const aResponse = await esFetch( pPath, pOptions );

    // text() and not json(): a refusal usually has no body at all, and json()
    // rejecting is precisely what hid these failures in the first place.
    const aText = await aResponse.text();

    let aParsed = null;
    try {
        aParsed = aText ? JSON.parse( aText ) : null;
    }
    catch {
        // Not JSON. A logged-out call answering the login page lands here, and
        // so does a proxy's error page; it is a failure whatever the status says.
        throw notJson( aResponse, 'GET', pPath );
    }

    if ( !aResponse.ok ) {
        const aError = new Error( aParsed?.msg || aParsed?.message
            || ( pPath + ' answered ' + aResponse.status ) );
        aError.status = aResponse.status;
        aError.body = aParsed;
        throw aError;
    }

    return aParsed;
}

/**
 * A query string, built rather than concatenated.
 *
 * <p>The codex used to have one query parameter in the whole app --
 * `API_ENTRY_SEARCH + "?q=" + value` -- and it was already wrong: the value was
 * pasted in raw, so a `&`, a `#` or a `+` in the search box either truncated
 * the term or silently changed it. The SRD ships items literally named
 * "+1 Weapon" and spells with commas in the title, so that stops being
 * theoretical the moment the import lands.
 *
 * <p>Now that every codex list is paginated there are three or four parameters
 * per call, so this builds them in one place. Null, undefined and empty-string
 * values are dropped rather than sent as `key=`, because Spring binds an empty
 * string to a String parameter and a section filter of "" is not the same
 * request as no section filter at all.
 *
 * <p>Returns "" for an empty parameter set, so a caller can always write
 * `path + esQuery( ... )`.
 */
export function esQuery( pParams = {} ) {
    const aSearch = new URLSearchParams();

    Object.entries( pParams ).forEach( ( [ aKey, aValue ] ) => {
        if ( aValue === null || aValue === undefined || aValue === '' ) return;
        aSearch.append( aKey, aValue );
    } );

    const aString = aSearch.toString();
    return aString ? '?' + aString : '';
}

/**
 * The XSRF token this browser was given, or ''.
 *
 * <p>esback sets it with CookieCsrfTokenRepository.withHttpOnlyFalse(), which
 * exists precisely so a script can read it back and echo it in a header --
 * that echo is what proves the request came from a page on this origin rather
 * than from a form on somebody else's.
 *
 * <p>Read from document.cookie rather than through react-cookie, because the
 * write helpers below are plain functions: a character sheet saves from an
 * onBlur, a wizard saves from a Finish button, and neither should have to be
 * inside a component that happens to have called useCookies to be able to.
 *
 * <p>And it is NOT the same string. This sentence read "the cookie is the
 * source either way -- react-cookie is reading the same string" until
 * 2026-09-21, which is the misconception that let a 403 into the login:
 * {@code useCookies} hands back getAll({doNotUpdate:true}), a parse CACHED when
 * the provider was created and refreshed by a change listener rather than by
 * the render a keystroke causes. document.cookie is the only reading that is
 * current at the moment of a write. See {@link esPrimedCsrfToken}.
 *
 * <p>EXPORTED so that {@link esSubmitLogout} -- which is a navigation and not a
 * fetch -- reads the token the same way every write does, rather than carrying
 * a second copy of the cookie regex that could drift from this one.
 */
export function esCsrfToken() {
    const aMatch = document.cookie.match( /(?:^|;\s*)XSRF-TOKEN=([^;]*)/ );
    return aMatch ? decodeURIComponent( aMatch[ 1 ] ) : '';
}

/**
 * The XSRF token, minting one first when this browser holds none.
 *
 * <p><b>A cold page holds no token, and since the login stopped being exempt
 * from CSRF that is a refusal rather than a shrug.</b> Escapade is one origin
 * in every deployed environment, and everything that is not {@code /api} is
 * answered by the nginx image -- so a browser that has just loaded the SPA has
 * not spoken to esback at all, and CookieCsrfTokenRepository has had no
 * response to write a cookie on. AuthProvider's authUser call at mount is what
 * mints it, and that call is IN FLIGHT while the page is already interactive.
 *
 * <p>A reader who reaches the login form and presses Sign in inside that window
 * posted no token: {@code undefined} in a fetch headers object is sent as the
 * literal string "undefined", CsrfFilter compares it with the real one and
 * answers 403 with an empty body, and the form reports "the server could not
 * sign you in (error 403)" about a password nothing ever read. It was invisible
 * while POST /api/v1/login sat on EsAuthConfig.csrfExemptions; the AQA browser
 * suite found it two scenarios later on the same account that had logged in
 * fine either side of them.
 *
 * <p>Reading document.cookie at the moment of the write is the other half.
 * {@code useCookies( ['XSRF-TOKEN'] )} hands back a react-cookie SNAPSHOT --
 * getAll({doNotUpdate:true}), a parse cached when the provider was created --
 * which is refreshed by a change listener and not by the render that a
 * keystroke causes. Every write here reads the string itself, so a token that
 * arrived a moment ago is the one that goes out.
 *
 * <p>authUser is the priming GET because it is permitAll, answers 200 with an
 * empty body to an anonymous caller, and is the call the application makes at
 * mount anyway. The machine clients prime on {@code GET /api/v1/login} instead
 * -- see EsAuthConfig.csrfExemptions -- because that path is the only address
 * they know reaches esback rather than a web server in front of it; a browser
 * has serverUrl and would only be buying a 405 in the console.
 */
export async function esPrimedCsrfToken() {
    const aToken = esCsrfToken();

    if ( aToken ) return aToken;

    await esFetch( API_AUTH_USER );

    return esCsrfToken();
}

/**
 * Log out, as a real browser navigation.
 *
 * <p><b>esWrite's navigating twin.</b> It needs the same two things every write
 * here needs -- esback's origin and the XSRF token -- and it needs neither of
 * the things a fetch gives it.
 *
 * <p><b>Why not a fetch.</b> esback answers this with a 302 to Cognito's hosted
 * logout URL, and that has to land in the ADDRESS BAR: Cognito signs the
 * browser out there and redirects it back by the logout_uri it was handed.
 * A fetch cannot put anything in the address bar, so whatever it makes of the
 * redirect, the half of the logout that happens at Cognito never happens.
 *
 * <p><b>Why not a link.</b> With CSRF on, Spring's LogoutFilter matches POST
 * only, so a GET falls through to anyRequest().authenticated() and logs nobody
 * out -- which is the point of the rule rather than an obstacle to it: a logout
 * a link can trigger is a logout another site can trigger. A generated form is
 * the one shape that is both a POST and a navigation.
 *
 * <p>The token goes in the {@code _csrf} REQUEST PARAMETER rather than a
 * header, because a form cannot set headers;
 * CsrfTokenRequestAttributeHandler on esback resolves the parameter when the
 * header is absent. It is read HERE, at the moment of the click, and not when
 * this module loaded -- the session may have been renewed since.
 *
 * <p><b>Why this lives in EsFetch.js rather than beside esHardNavigate.</b>
 * EsEnv.js is the better neighbour by subject and the wrong one by dependency:
 * this module already imports serverUrl FROM EsEnv, so putting this there and
 * importing esCsrfToken back would close a cycle between the two.
 */
export function esSubmitLogout() {
    esSessionLeaving();
    // The next reader in this tab starts clean: the codex's pack picks were the
    // account's, and sessionStorage outlives the round trip through Cognito.
    try {
        window.sessionStorage.removeItem( CODEX_PICKS_STORAGE );
    }
    catch {
        // A browser that refuses storage stored nothing to clear.
    }

    const aForm = document.createElement( 'form' );
    aForm.method = 'post';
    aForm.action = serverUrl + API_LOGOUT;

    const aToken = document.createElement( 'input' );
    aToken.type = 'hidden';
    aToken.name = '_csrf';
    aToken.value = esCsrfToken();
    aForm.appendChild( aToken );

    // In the document, because a detached form does not submit. Removed again
    // straight after: the request is already dispatched by then, and a
    // navigation that never happens must not leave a stray form in the page.
    document.body.appendChild( aForm );
    aForm.submit();
    aForm.remove();
}

/**
 * Leave after deleting your own account. esback has already ended the session and expired its
 * cookies, so this is a full load of home -- every cached piece of the signed-in app goes with
 * the old page -- where EsAccountDeletedNote reads the flag left here.
 */
export function esLeaveAfterAccountDelete() {
    esSessionLeaving();
    try {
        window.sessionStorage.removeItem( CODEX_PICKS_STORAGE );
        window.sessionStorage.setItem( ACCOUNT_DELETED_STORAGE, '1' );
    }
    catch {
        // No storage, no note: the home page still loads signed out.
    }

    window.location.assign( '/' );
}

/**
 * A write to esback.
 *
 * <p>Every write in this app was hand-copying the same four lines -- the
 * X-XSRF-TOKEN header, Accept, Content-Type and credentials -- and the plan
 * (§6.5) called for this before characters, which brings more writes than the
 * rest of the app has together. Miss the token and esback answers 403 with
 * nothing in it that says which header was wrong.
 *
 * <p>Returns the parsed body on 2xx and THROWS on anything else, carrying the
 * status and whatever {"msg": ...} envelope esback sent. The old pattern --
 * .then( response => response.json() ) with no status check -- turns a 403 into
 * a successful parse of an error object, which the caller then saves into its
 * state as if it were the row it asked for. A 404 from EsFlagFilter does
 * exactly that.
 *
 * <p>204 and an empty body answer null rather than failing to parse, which is
 * what DELETE returns.
 */
export async function esWrite( pPath, pMethod, pBody ) {
    const aResponse = await esFetch( pPath, {
        method: pMethod,
        headers: {
            'X-XSRF-TOKEN': await esPrimedCsrfToken(),
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
        body: pBody === undefined ? undefined : JSON.stringify( pBody ),
    } );

    return written( aResponse, pMethod, pPath );
}

/**
 * The half of a write that is the same whatever was sent.
 *
 * <p>Lifted out when esUpload arrived rather than copied into it, because the
 * contract these eleven lines implement is the thing every caller in the app
 * relies on -- 2xx answers the parsed body, anything else THROWS carrying
 * esback's {"msg": ...} and the status, and an empty body answers null. Two
 * copies of it is two chances for a verb to drift into resolving on a refusal,
 * which is the exact failure esWrite's javadoc above was written about.
 */
async function written( pResponse, pMethod, pPath ) {
    const aText = await pResponse.text();
    let aParsed = null;
    try {
        aParsed = aText ? JSON.parse( aText ) : null;
    }
    catch {
        throw notJson( pResponse, pMethod, pPath );
    }

    if ( !pResponse.ok ) {
        const aError = new Error( aParsed?.msg || aParsed?.message
            || ( pMethod + ' ' + pPath + ' answered ' + pResponse.status ) );
        aError.status = pResponse.status;
        aError.body = aParsed;
        throw aError;
    }

    return aParsed;
}

/**
 * The error for an answer that is not JSON where esback always answers JSON: the load
 * balancer's or nginx's HTML error page while esback is down or restarting (B22, 2026-10-29,
 * where a picture's upload drew "Unexpected token '<', "<html> <h"... is not valid JSON"), or
 * a login page. The reader gets a sentence they can act on; New Relic gets an error event,
 * which is the alert -- an outage is exactly when esback cannot be told.
 */
export const NOT_ANSWERING = 'The server is not answering right now. Try again in a minute.';
export const ANSWERED_ODDLY = 'The server answered something unexpected. Try again.';

function notJson( pResponse, pMethod, pPath ) {
    const aDown = [ 502, 503, 504 ].includes( pResponse.status );
    const aError = new Error( aDown ? NOT_ANSWERING : ANSWERED_ODDLY );
    aError.status = pResponse.status;
    aError.notJson = true;
    // The path without its query, which can carry a search or an address.
    esRumError( aError, { esPath: String( pPath ).split( '?' )[ 0 ], esMethod: pMethod, esStatus: pResponse.status } );
    return aError;
}

/**
 * A multipart POST -- a file, rather than a document.
 *
 * <p><b>Why esWrite cannot do this.</b> It sets Content-Type:
 * application/json and JSON.stringifies the body, and neither is survivable
 * here: a FormData run through JSON.stringify is the string "{}", and the
 * header is worse than the body.
 *
 * <p><b>The header is the trap, so it is worth being exact about.</b> A
 * multipart request's Content-Type is not `multipart/form-data` -- it is
 * `multipart/form-data; boundary=----WebKitFormBoundary...`, and the boundary
 * is a random token the browser generates WHEN IT SERIALISES THE BODY and puts
 * in front of every part. Set the header by hand and fetch stops generating
 * one: the body still carries whatever boundary the browser chose and the
 * header now names a different one, or none. esback's parser then finds no
 * parts, and Spring answers 415 Unsupported Media Type -- a status that reads
 * as "wrong file type" and is nothing of the kind. So this function sets
 * Accept and the CSRF token and DELIBERATELY sets no Content-Type: the one
 * header a multipart request needs is the one nobody may write.
 *
 * <p>Everything else is esWrite's, on purpose. Same X-XSRF-TOKEN through
 * esPrimedCsrfToken -- an upload is a state change and Spring's CSRF filter
 * does not care that it is a file -- same credentials through esFetch, and the
 * same throw-on-refusal contract, so a caller can still
 * `.catch( e => e.message )` whichever verb it used.
 *
 * <p>`pMethod` is POST unless the endpoint replaces in place: the copykeys
 * admin PUTs a variant's file.
 *
 * <p>The FIELD NAME is the caller's, set when it appends to the FormData, not
 * a parameter here -- the multipart field name is part of the endpoint's
 * contract and belongs at the call site that knows the endpoint.
 */
export async function esUpload( pPath, pFormData, pMethod = 'POST' ) {
    const aResponse = await esFetch( pPath, {
        method: pMethod,
        headers: {
            'X-XSRF-TOKEN': await esPrimedCsrfToken(),
            'Accept': 'application/json',
        },
        body: pFormData,
    } );

    return written( aResponse, pMethod, pPath );
}

/**
 * A POST whose body is a file as it is -- no JSON, no multipart frame -- for an endpoint
 * that reads its request body as the file (an importer's attached PDFs, each larger than
 * the multipart ceiling). The CSRF token and the throw-on-refusal contract are esWrite's.
 */
export async function esSend( pPath, pFile, pType ) {
    const aResponse = await esFetch( pPath, {
        method: 'POST',
        headers: {
            'X-XSRF-TOKEN': await esPrimedCsrfToken(),
            'Accept': 'application/json',
            'Content-Type': pType || pFile.type || 'application/octet-stream',
        },
        body: pFile,
    } );

    return written( aResponse, 'POST', pPath );
}

export const esPost = ( pPath, pBody ) => esWrite( pPath, 'POST', pBody );
export const esPut = ( pPath, pBody ) => esWrite( pPath, 'PUT', pBody );
export const esDelete = ( pPath ) => esWrite( pPath, 'DELETE' );
